<script setup>
import SelectBoxComponent from '@/components/SelectBoxWithRegistComponent.vue';
import { useAxios } from '@/hooks/useAxios';
import { computed, onMounted, ref, watch } from 'vue';

// Props 정의
const props = defineProps({
    modelValue: {
        type: Object,
        default: () => []
    },
    inputId: {
        type: String,
        default: ''
    },
    options: {
        type: Array,
        default: () => []
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'select', 'update-options']);
const isReadonly = computed(() => props.readonly);
const inputValue = ref(props.modelValue);
const selectValue = ref('');
const deliId = ref(props.deliveryId);
const errMessage = ref(props.errmsg);
const ValidationMsg = ref('');
const vehicleGroupList = ref([]);
const selectedItems = ref([]);
const structuredData = ref({});
const disabledItems = ref([]);

onMounted(() => {
    //console.log(deliId.value);
    selectValue.value = '';
    setVehicleGroup();
});

const updateItems = (options) => {};

const setVehicleGroup = () => {
    try {
        let url = '/api/vehicle-group';
        //console.log(url);
        const config = {
            method: 'get'
        };
        const { data, error, execute } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                vehicleGroupList.value = response.data.data.map((vehicle) => ({
                    value: vehicle.vehicle_group_id,
                    text: vehicle.vehicle_group_name
                }));
                //console.log(vehicleGroupList);

                // structuredData.value = processData(response.data.data);
                // initializeSelectedItems(response.data.data);
            },
            onError(err) {
                console.error('Error:', err.message);
            }
        });

        execute();
    } catch (error) {
        console.error('구역 조회 오류 발생:', error);
    }
};

const initializeSelectedItems = (data) => {
    selectedItems.value = data.filter((item) => item.assign_state === 'DONE_ALLOW');
    disabledItems.value = data.filter((item) => item.assign_state === 'DONE_DENY');
    updateSelections();
};

const cities = computed(() => Object.keys(structuredData.value));
const districts = computed(() => {
    if (selectedCity.value && selectedCounty.value) {
        return ['전체', ...structuredData.value[selectedCity.value][selectedCounty.value]];
    }
    return [];
});

// 수정된 formatItems 함수
const formatItems = (items, columns) => {
    const result = [];
    for (let i = 0; i < items.length; i += columns) {
        result.push(items.slice(i, i + columns));
    }
    return result;
};

// 수정된 computed 속성
const formattedCities = computed(() => formatItems(cities.value, 2));

const updateSelections = () => {
    if (selectedCity.value) {
        // 선택된 city에 대한 county 목록 업데이트
        const countiesForCity = [...new Set(selectedItems.value.filter((item) => item.city === selectedCity.value).map((item) => item.county))];

        if (countiesForCity.length > 0 && !selectedCounty.value) {
            selectedCounty.value = countiesForCity[0];
        }

        if (selectedCounty.value) {
            // 선택된 city와 county에 대한 district 목록 업데이트
            const selectedDistrictsForCurrentCounty = selectedItems.value.filter((item) => item.city === selectedCity.value && item.county === selectedCounty.value).map((item) => item.district);

            selectedDistricts.value = selectedDistrictsForCurrentCounty;

            // '전체' 항목 처리
            if (districts.value && selectedDistrictsForCurrentCounty.length === districts.value.length - 1) {
                selectedDistricts.value.push('전체');
            }
        }
    }
};

const removeSelectedItem = (index) => {
    selectedItems.value.splice(index, 1);
    emitUpdate(selectedItems.value);
};

const removeAll = () => {
    if (selectedItems.value.length === 0) return;
    selectedItems.value = [];
    emitUpdate(selectedItems.value);
};

const emitUpdate = (value) => {
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        value: value
    });
};

const selectItem = (event) => {
    const selectedValue = event.value;
    const selectedItem = vehicleGroupList.value.find((item) => item.value === selectedValue);

    if (selectedItem && !selectedItems.value.some((item) => item.value === selectedValue)) {
        selectedItems.value.push(selectedItem);
        emitUpdate(selectedItems.value);
    }
};

watch(
    () => props.modelValue,
    (newVal) => {
        inputValue.value = newVal || '';
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

watch(
    () => props.options,
    (newOptions) => {
        updateItems(newOptions);
    },
    { immediate: true }
);
</script>

<template>
    <div
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <!-- <label :for="inputId" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label> -->

        <SelectBoxComponent inputId="selectbox" :modelValue="selectValue" :options="vehicleGroupList" class="selectbox-container" :label="props.label" @input="selectItem"> </SelectBoxComponent>
        <div class="select-count"><button @click="removeAll()" class="removeall-button">전체취소</button> {{ selectedItems.length }} 건</div>
        <div class="selected-items-container">
            <div v-for="(item, index) in selectedItems" :key="index" class="selected-item">
                <span class="item-text">{{ item.text }}</span>
                <button @click="removeSelectedItem(index)" class="remove-button">X</button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171;
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.selectbox-container {
    display: flex;
    width: 100%;
}

.button-grid {
    display: grid;
    gap: 0.5rem;
}

.button-row {
    display: flex;
    gap: 0.5rem;
}

.grid-button {
    flex: 1;
    padding: 0.3rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    color: #374151;
    background-color: white;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.3rem;
    cursor: pointer;
    transition:
        background-color 0.2s,
        box-shadow 0.2s;
    min-height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.grid-button:hover {
    background-color: #eef0f1;
}

.grid-button:focus {
    outline: none;
}

.grid-button.selected {
    box-shadow: 0 0 0 0.1rem #0038ea;
    opacity: 0.9;
}

.selected-items-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.2rem;
    margin-top: 0.1rem;
    height: 13.2rem;
    overflow-y: auto;
    padding: 0.5rem 0.4rem;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.5rem;
    justify-content: flex-start;
    align-content: flex-start;
    width: 100%;
}

.selected-item {
    background-color: #f1f3f4;
    padding: 0.6rem;
    border-radius: 0.3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    height: 1.7rem;
    margin-right: 0.2rem;
    margin-bottom: 0.2rem;
    width: calc(33.33% - 0.35rem);
    box-sizing: border-box;
}

.item-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-grow: 1;
    margin-left: 0.3rem;
}

.remove-button {
    margin-left: 0.5rem;
    color: red;
    width: 0.5rem;
    font-family: 'SCoreDream400';
    font-size: 0.75rem;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background: none;
    border: none;
    margin-right: 0.2rem;
}

.remove-button:hover {
    font-family: 'SCoreDream600';
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.grid-button.disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.select-count {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    opacity: 0.7;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 0.35rem;
    margin-top: 1rem;
    margin-right: 0.25rem;
    height: 0.5rem;
}

.removeall-button {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    opacity: 0.8;
    cursor: pointer;
    margin-right: 1rem;
}

.removeall-button:hover {
    opacity: 1;
    cursor: pointer;
}
</style>
